<template>
  <div v-if="items.length < 1">
    {{$t('views.exsborder.list.noOrders')}}
  </div>
  <table v-else class="table table-sm table-grid table-responsive-md">
    <thead>
    <tr>
      <th>{{$t('views.exsborder.list.orderStatus')}}</th>
      <th>{{$t('views.exsborder.list.valueNetto')}}</th>
      <th>{{$t('views.exsborder.list.valueBrutto')}}</th>
      <th>{{$t('views.exsborder.list.firm')}}</th>
      <th>{{$t('views.exsborder.list.tags')}}</th>
      <th/>
    </tr>
    </thead>
    <tbody v-for="(item, index) in sortedOrders" :key="item['@id']" @click="expand(index)" class="hover-row">
    <tr v-bind:class="{'table-success': item.completed, 'table-warning': item.priced}">
      <td>
        <span v-if="item.priced">{{$t('views.exsborder.list.priced')}}, </span>
        <span v-else-if="item.completed">{{$t('views.exsborder.list.completed')}}, </span>
        {{ item['orderStatus'].name }}
      </td>
      <td>
        {{ item['valueNetto'] }}
      </td>
      <td>
        {{ item['valueBrutto'] }}
      </td>
      <td>
        {{ item.firm.firmname1 === '' ? item.firm.fullName : item.firm.firmname1 }}
      </td>
      <td>
        <span v-for="tag in item.orderTags" :key="tag['@id']">
          <b-badge class="mr-2 mb-2 organizationleadtag" pill :style="{'background-color': tag.tag.color}"><i class="fa fa-tags mr-2"></i>{{tag.tag.tag}}</b-badge>
        </span>
      </td>
      <td>
        <div class="float-right">
          <b-dropdown size="sm" variant="link" right text="Right align" toggle-class="text-decoration-none" no-caret>
            <template #button-content>
              <i class="fa fa-bars fa-lg"></i>
            </template>
            <b-dropdown-item
              @click="showOrder(item['@id'])"
              @click.native.stop
              aria-hidden="true">
              <i class="fa fa-search fa-lg"></i>
              {{ $t('buttons.show') }}
            </b-dropdown-item>
            <b-dropdown-item
              v-if="getIdFromLink(item.orderStatus['@id']) === PLACED && ableToEditOrder"
              @click="editOrder(item['@id'])"
              @click.native.stop
            >
              <i class="fa fa-edit fa-lg">
              </i>
              {{ $t('buttons.edit') }}
            </b-dropdown-item>
            <b-dropdown-divider></b-dropdown-divider>
            <b-dropdown-item
              v-if="ableToChangeStatusOrder"
              @click="changeOrderStatus(item['@id'])"
              @click.native.stop
            >
              <i class="fas fa fa-cogs fa-lg"></i>
              {{ $t('buttons.changeStatus') }}
            </b-dropdown-item>
            <b-dropdown-item
              v-if="ableToChangeStatusOrder"
              @click="completeOrder(item['@id'])"
              @click.native.stop
            >
              <i class="fas fa fa-check fa-lg"></i>
              {{ $t('buttons.completeOrder') }}
            </b-dropdown-item>
            <b-dropdown-item
              v-if="ableToPriceOrder"
              @click="priceOrder(item['@id'])"
              @click.native.stop
            >
              <i class="fas fa fa-check fa-lg"></i>
              {{ $t('buttons.priceOrder') }}
            </b-dropdown-item>
            <b-dropdown-item
              v-if="ableToDuplicateOrder"
              @click="duplicate(item['@id'])"
              @click.native.stop
            >
              <i class="fas fa fa-copy fa-lg"></i>
              {{ $t('buttons.duplicate') }}
            </b-dropdown-item>
            <b-dropdown-item
              v-if="ableToExtractOrder"
              @click="extractOrder(item['@id'])"
              @click.native.stop
            >
              <i class="fas fa fa-copy fa-lg"></i>
              {{ $t('buttons.extractOrder') }}
            </b-dropdown-item>
            <b-dropdown-item v-if="item.state === 1 && ableToArchiveOrder"
             @click="archiveItem(item['@id'])"
             @click.native.stop
            >
              <i class="fas fa fa-file-archive fa-lg"></i>
              {{ $t('buttons.archive') }}
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </td>
    </tr>
    <tr v-bind:class="{'table-success': item.completed}">
      <td colspan="6" class="py-0 px-0">
        <b-collapse :id="'order_positions_collapse_'+index" v-model="item.expanded">
          <positions-table :positions="item.positions"></positions-table>
        </b-collapse>
      </td>
    </tr>
    </tbody>
    <show-order-modal :id-order="idOrder"></show-order-modal>
  </table>
</template>

<script>
import ShowOrderModal from '@/components/Dashboard/Views/BusinessBundle/exsborder/modals/ShowModal'
import { mapMutations } from 'vuex'
import getId from '@/utils/getIdFromLink'
import ApiService from '@/utils/ApiService'
import PositionsTable from '@/components/Dashboard/Views/BusinessBundle/exsborder/components/PositionsTable'
import { checkPermission, getUserRoles } from '@/utils/checkRoles'
import { archive } from '@/utils/archive'

export default {
  name: 'CaseOrderTable',

  components: {
    PositionsTable,
    ShowOrderModal
  },

  props: ['items'],

  computed: {
    inactiveStatuses () {
      return [this.CANCELLED, this.ARCHIVED, this.COMPLETED]
    },
    sortedOrders () {
      let orders = [...this.items].sort((current, next) => this.comparePositions(current, next))
      orders.forEach((order) => {
        order['expanded'] = true
      })
      return orders
    },
    isWholesaler () {
      return getUserRoles().roles.includes('ROLE_WHOLESALER')
    },
    ableToEditOrder () {
      if (this.isWholesaler) return false
      return checkPermission('orders', 'EXSBOrderUpdate')
    },
    ableToChangeStatusOrder () {
      if (!checkPermission('orders', 'EXSBOrderStatusChange')) return false
      return true
    },
    ableToPriceOrder () {
      if (!checkPermission('orders', 'EXSBOrderPrice')) return false
      return true
    },
    ableToDuplicateOrder () {
      if (this.isWholesaler) return false
      return checkPermission('orders', 'EXSBOrderCreate')
    },
    ableToExtractOrder () {
      if (!checkPermission('orders', 'EXSBOrderExtract')) return false
      if (this.isWholesaler) return false
      return true
    },
    ableToArchiveOrder () {
      if (this.isWholesaler) return false
      return checkPermission('orders', 'EXSBOrderUpdate')
    },
    ableToDeleteOrder () {
      if (this.isWholesaler) return false
      return checkPermission('orders', 'EXSBOrderUpdate')
    }
  },

  data () {
    return {
      PLACED: 1,
      ACCEPTED: 2,
      PENDING: 3,
      COMPLETED: 4,
      CANCELLED: 5,
      ARCHIVED: 6,
      idOrder: null,
      orderStatuses: {}
    }
  },

  mounted () {
    this.getOrderStatuses()
  },

  methods: {
    async extractOrder (id) {
      const decision = await this.$Swal({
        title: this.$t('views.exsborder.create.extractQuestion'),
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('buttons.yes'),
        cancelButtonText: this.$t('buttons.no')
      })
      if (decision) {
        await this.extractOrderHandler(id)
      }
    },
    async extractOrderHandler (id) {
      this.$emit('extract', id)
      this.$emit('reload')
    },
    async completeOrder (id) {
      const decision = await this.$Swal({
        title: this.$t('views.exsborder.create.completeQuestion'),
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('buttons.yes'),
        cancelButtonText: this.$t('buttons.no')
      })
      if (decision) {
        await this.completeOrderHandler(id)
      }
    },
    async priceOrder (id) {
      const decision = await this.$Swal({
        title: this.$t('views.exsborder.create.priceQuestion'),
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('buttons.yes'),
        cancelButtonText: this.$t('buttons.no')
      })
      if (decision) {
        await this.priceOrderHandler(id)
      }
    },
    async completeOrderHandler (id) {
      this.$emit('complete', id)
      this.$emit('reload')
    },
    async priceOrderHandler (id) {
      this.$emit('price', id)
      this.$emit('reload')
    },
    async duplicate (id) {
      const decision = await this.$Swal({
        title: this.$t('views.exsborder.create.duplicateQuestion'),
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('buttons.yes'),
        cancelButtonText: this.$t('buttons.no')
      })
      if (decision) {
        await this.duplicateOrderHandler(id)
      }
    },
    async duplicateOrderHandler (id) {
      this.$emit('duplicate', id)
      this.$emit('reload')
    },
    expand (index) {
      this.sortedOrders[index].expanded = !this.sortedOrders[index].expanded
      this.$forceUpdate()
    },
    comparePositions (current, next) {
      if (this.isInactive(current) !== this.isInactive(next)) {
        if (this.isInactive(current)) {
          return 1
        } else {
          return -1
        }
      }
      if (this.$moment(current.updatedAt).isBefore(this.$moment(next.updatedAt))) {
        return 1
      }
      if (this.$moment(current.updatedAt).isAfter(this.$moment(next.updatedAt))) {
        return -1
      }
      return 0
    },
    isInactive (order) {
      return this.inactiveStatuses.includes(this.getStatus(order))
    },
    getStatus (order) {
      return parseInt(getId(order.orderStatus['@id']))
    },
    async changeOrderStatus (id) {
      const newStatus = await this.$Swal({
        showConfirmButton: true,
        confirmButtonText: this.$t('buttons.select'),
        showCancelButton: true,
        cancelButtonText: this.$t('buttons.cancel'),
        title: this.$t('buttons.changeStatus'),
        input: 'select',
        inputOptions: this.orderStatuses
      })
      if (newStatus) {
        let data = {
          '@id': id,
          orderStatus: newStatus
        }
        const response = await new ApiService(`e_x_s_b_orders/${getId(id)}`).put(data)
        if (response.status === 200) {
          this.$emit('reload')
        }
      }
    },
    showOrder (id) {
      if (this.$route.name === 'EXSBOrderList') {
        this.$router.push({ name: 'EXSBOrderShow', params: { id: id } })
      } else {
        this.toggleShowOrderModal(id)
      }
    },
    editOrder (id) {
      this.$router.push({ name: 'EXSBOrderUpdate', params: { id: id } })
    },
    toggleShowOrderModal (id) {
      this.idOrder = id
      this.toggleModal()
    },
    ...mapMutations({
      toggleModal: 'exsborder/showModal/toggleModal'
    }),
    getIdFromLink (link) {
      return parseInt(getId(link))
    },
    async getOrderStatuses () {
      let params = {
        'pagination': false,
        'state': 1
      }
      const response = await new ApiService('e_x_s_b_order_statuses', params).getResults()
      response.items.forEach(status => {
        this.orderStatuses[status['@id']] = status.name
      })
    },
    archiveItem (id) {
      archive(id, () => { this.$emit('reload') })
    }
  }
}
</script>

<style scoped>
.hover-row:hover {
  background-color: #f7f7f9;
}
</style>
